import { CdkTable } from '@angular/cdk/table';
import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   DoCheck,
   EventEmitter,
   Input,
   OnDestroy,
   OnInit,
   Output,
   ViewChild,
   ViewEncapsulation,
} from '@angular/core';
import {
   AbstractControl,
   AbstractControlOptions,
   ControlValueAccessor,
   FormGroupDirective,
   NG_VALUE_ACCESSOR,
   UntypedFormArray,
   UntypedFormBuilder,
   UntypedFormGroup,
   ValidationErrors,
   ValidatorFn,
   Validators,
} from '@angular/forms';
import { CallState } from '@lcs/shared/api';
import { Store } from '@ngrx/store';
import { ExtensionTypes, NullActionTypes } from 'generated/graphql-types';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { getNullActionPlaceholder } from 'src/app/pages/extension-details/helpers/null-action-helper';
import { ExtensionPrivileges } from 'src/app/pages/extension-details/models/extension-privileges';
import { DialogSize } from '../confirmation-dialog/enumerations/dialog-size.enum';
import { AutoAttendantKeyItemView } from './models/auto-attendant-key-item.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [{ multi: true, provide: NG_VALUE_ACCESSOR, useExisting: AutoAttendantKeyListComponent }],
    selector: 'ndt-auto-attendant-key-list',
    styleUrls: ['./auto-attendant-key-list.component.scss'],
    templateUrl: './auto-attendant-key-list.component.html',
    standalone: false
})
export class AutoAttendantKeyListComponent implements OnDestroy, OnInit, ControlValueAccessor, DoCheck {
   @ViewChild('table') table: CdkTable<UntypedFormGroup>;

   @Input() accountId: number;

   @Input() extensionId: number;

   @Input() callState: CallState;

   @Input() canEdit: boolean;

   @Input() canDelete: boolean;

   @Input() showCreate: boolean;

   @Input() form: UntypedFormGroup;

   @Input() extensionPrivileges: ExtensionPrivileges;

   @Output() deleteAutoAttendantKey = new EventEmitter<{ autoAttendantKeyId: number }>();

   columns: string[] = ['keyNumber', 'routeToExtension', 'voiceMailOnly', 'routeToNullAction', 'deleteKey'];

   autoAttendantKeysArray: UntypedFormArray;

   form$: BehaviorSubject<UntypedFormGroup>;

   disabled = false;

   onTouched: () => void;

   private onChange: (value: AutoAttendantKeyItemView[] | null) => void;

   private autoAttendantKeyItems: AutoAttendantKeyItemView[] = [];

   private destroyed$ = new Subject<void>();

   controlOptions: AbstractControlOptions = { validators: routeSelectionValidator() };

   selectedOption: string | null = null;

   nullActions = [NullActionTypes.Directory, NullActionTypes.Hangup];

   constructor(
      private changeDetectorRef: ChangeDetectorRef,
      private fb: UntypedFormBuilder,
      public store: Store,
      private confirmationService: ConfirmationDialogService,
      private parentForm: FormGroupDirective
   ) {
      this.autoAttendantKeysArray = this.fb.array([], [keyValidator(), Validators.required]);

      this.form$ = new BehaviorSubject<UntypedFormGroup>(
         new UntypedFormGroup({
            autoAttendantKeysArray: this.autoAttendantKeysArray,
         })
      );
   }

   ngOnInit(): void {
      this.form$
         .pipe(
            switchMap((form: UntypedFormGroup) => {
               this.modifyEditState();
               return form.valueChanges;
            }),
            takeUntil(this.destroyed$)
         )
         .subscribe(() => {
            const autoAttendantKeysArrayControl = this.form$.value.get('autoAttendantKeysArray');
            this.onChange(autoAttendantKeysArrayControl?.value as AutoAttendantKeyItemView[]);

            this.changeDetectorRef.markForCheck();
            this.form$.value.updateValueAndValidity({ emitEvent: false });
         });
   }

   ngDoCheck(): void {
      // We cannot subscribe to the parent form being submitted, so we check if the parent form is invalid
      // If it is and the keys have been touched (happens on submit), we need to show validation errors
      // this method runs constantly, we need to keep it as light as possible
      if (
         this.parentForm.form.invalid &&
         this.parentForm.form.get(['extensionAutoAttendant', 'autoAttendantKeys'])?.touched
      ) {
         this.autoAttendantKeysArray.markAllAsTouched();
         this.changeDetectorRef.markForCheck();
      }
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   trackBy(_index: number, item: AutoAttendantKeyItemView): number {
      return item.autoAttendantKeyId;
   }

   handleRouteToExtensionChange(keyFormGroup: UntypedFormGroup): void {
      const { routeToExtension, voiceMailOnly, routeToNullAction, selectedOption } = keyFormGroup.controls;

      if (routeToNullAction?.value !== NullActionTypes.Unset) {
         routeToExtension?.clearValidators();
         routeToExtension?.setValidators([]);
      } else {
         routeToExtension?.setValidators(Validators.required);
      }

      routeToExtension?.updateValueAndValidity();

      if (routeToExtension?.value == null || routeToExtension?.value?.extensionId == null) {
         if (routeToNullAction?.value === NullActionTypes.Directory) {
            selectedOption?.setValue('Name Directory');
         } else if (routeToNullAction?.value === NullActionTypes.Hangup) {
            selectedOption?.setValue('Hangup');
         }
         if (voiceMailOnly?.value) {
            voiceMailOnly?.setValue(false);
         }
      } else {
         selectedOption?.setValue(null);
         if (routeToExtension?.value.extensionId === -1) {
            routeToNullAction?.setValue(NullActionTypes.Directory);
            routeToExtension?.setValue({
               extensionId: null,
               name: getNullActionPlaceholder(NullActionTypes.Directory),
               number: -1,
            });
            return;
         } else if (routeToExtension?.value.extensionId === -2) {
            routeToNullAction?.setValue(NullActionTypes.Hangup);
            routeToExtension?.setValue({
               extensionId: null,
               name: getNullActionPlaceholder(NullActionTypes.Hangup),
               number: -2,
            });
            return;
         } else {
            routeToNullAction?.setValue(NullActionTypes.Unset);
         }
         if (routeToExtension?.value.type !== ExtensionTypes.Phone) {
            voiceMailOnly?.setValue(false);
         }
      }
   }
   onRadioButtonChange(index: number, controlName: string, value: boolean): void {
      const keysArray = this.form$.value.get('autoAttendantKeysArray');
      const keyControl = keysArray?.get(index.toString()) as UntypedFormGroup;

      if (controlName === 'voiceMailOnly') {
         keyControl.get('voiceMailOnly')?.setValue(value);
      }

      this.autoAttendantKeysArray.updateValueAndValidity();
   }

   deleteConfirmationDialog(): Observable<boolean> {
      const message = 'Changes will not take effect until you save the form.';

      const dialogRef = this.confirmationService.confirm({
         data: {
            header: 'Delete this key?',
            message,
         },
         size: DialogSize.Medium,
      });

      return dialogRef.afterClosed().pipe(
         takeUntil(this.destroyed$),
         switchMap((confirmed) => {
            if (confirmed) {
               return of(true);
            } else {
               return of(false);
            }
         })
      );
   }

   onDelete(key: AutoAttendantKeyItemView, rowIndex: number): void {
      if (this.extensionPrivileges?.canEditAutoAttendants) {
         if (this.autoAttendantKeysArray.length > 0) {
            this.deleteConfirmationDialog()
               .pipe(takeUntil(this.destroyed$))
               .subscribe((confirmed) => {
                  if (confirmed) {
                     this.autoAttendantKeyItems = this.autoAttendantKeysArray.value;
                     this.autoAttendantKeyItems[rowIndex].isDeleted = true;
                     this.autoAttendantKeysArray.patchValue(this.autoAttendantKeyItems);
                     this.onChange(this.autoAttendantKeyItems);

                     this.clearErrorsFromDeletedKeys();
                     this.autoAttendantKeysArray.updateValueAndValidity();
                  }
               });
         }
      }
   }

   writeValue(keys: AutoAttendantKeyItemView[] | null): void {
      if (keys) {
         this.autoAttendantKeyItems = keys.map((key) => {
            return {
               ...key,
               isDeleted: false,
               selectedOption:
                  key.routeToNullAction == NullActionTypes.Directory && key.routeToExtension === null
                     ? 'Name Directory'
                     : key.routeToNullAction == NullActionTypes.Hangup && key.routeToExtension === null
                     ? 'Hangup'
                     : null,
            };
         });

         if (this.autoAttendantKeyItems.length === 0) {
            this.autoAttendantKeysArray = this.fb.array([], [keyValidator(), Validators.required]); // Added requiredArrayValidator
            this.autoAttendantKeyItems.forEach((key) => {
               this.autoAttendantKeysArray.insert(this.autoAttendantKeyItems.length, this.createKeyFormGroup(key));
            });
            this.form$.next(
               new UntypedFormGroup({
                  autoAttendantKeysArray: this.autoAttendantKeysArray,
               })
            );
         } else if (this.autoAttendantKeyItems.length > 0) {
            this.autoAttendantKeysArray.clear();
            this.autoAttendantKeyItems.forEach((key) => {
               if (key.routeToNullAction != NullActionTypes.Unset && key.routeToExtension === null) {
                  key.routeToExtension = null;
               }
               this.autoAttendantKeysArray.insert(this.autoAttendantKeyItems.length, this.createKeyFormGroup(key), {
                  emitEvent: false,
               });
            });
            this.form$.next(
               new UntypedFormGroup({
                  autoAttendantKeysArray: this.autoAttendantKeysArray,
               })
            );
         }
         this.changeDetectorRef.markForCheck();
      }
      if (this.table) {
         this.table.renderRows();
      }
   }

   registerOnChange(onChange: (value: AutoAttendantKeyItemView[] | null) => void): void {
      this.onChange = (value: AutoAttendantKeyItemView[] | null) => {
         if (!this.disabled) {
            onChange(value);
         }
      };
   }

   registerOnTouched(onTouched: () => void): void {
      this.onTouched = onTouched;
   }

   setDisabledState(isDisabled: boolean): void {
      this.disabled = isDisabled;
      this.changeDetectorRef.markForCheck();
   }

   onCreate(): void {
      const newKey: AutoAttendantKeyItemView = {
         autoAttendantKeyId: 0,
         extensionAutoAttendantId: 0,
         isDeleted: false,
         keyNumber: null,
         routeToExtension: null,
         voiceMailOnly: false,
         routeToNullAction: NullActionTypes.Unset,
         selectedOption: null,
      };
      this.autoAttendantKeyItems.push(newKey);
      const newKeyFormGroup = this.createKeyFormGroup(newKey);

      this.autoAttendantKeysArray.insert(this.autoAttendantKeysArray.length, newKeyFormGroup);

      this.autoAttendantKeysArray.controls.forEach((keyControl) => {
         keyControl.updateValueAndValidity({ emitEvent: false });
      });

      if (this.table) {
         this.table.renderRows();
      }
   }

   modifyEditState() {
      if (!this.canEdit) {
         this.form$.value.disable();
      }
   }

   isExtensionTypePhone(index: number): boolean {
      const keysArray = this.form$.value.get('autoAttendantKeysArray');
      const extensionType = (keysArray?.value[index] as AutoAttendantKeyItemView).routeToExtension?.type;
      if (extensionType) {
         return extensionType === ExtensionTypes.Phone;
      }

      return false;
   }

   private createKeyFormGroup(key?: AutoAttendantKeyItemView): UntypedFormGroup {
      const formGroup = this.fb.group(
         {
            autoAttendantKeyId: key?.autoAttendantKeyId,
            isDeleted: key?.isDeleted ?? false,
            keyNumber: this.fb.control(key?.keyNumber, [keyValidator(), Validators.required]),
            routeToExtension: this.fb.control(key?.routeToExtension, []),
            voiceMailOnly: this.fb.control(key?.voiceMailOnly),
            routeToNullAction: this.fb.control(key?.routeToNullAction),
            selectedOption: this.fb.control(key?.selectedOption),
         },
         this.controlOptions
      );
      formGroup.get('keyNumber')?.updateValueAndValidity({ emitEvent: false });
      return formGroup;
   }
   private clearErrorsFromDeletedKeys(): void {
      this.autoAttendantKeysArray.controls.forEach((control: AbstractControl) => {
         const isDeleted = control.get('isDeleted')?.value;
         if (isDeleted) {
            (control as UntypedFormGroup).controls &&
               Object.values((control as UntypedFormGroup).controls).forEach((ctrl) => {
                  ctrl.setErrors(null);
                  ctrl.markAsPristine();
                  ctrl.markAsUntouched();
               });
            control.setErrors(null);
         }
      });
   }
}

export function keyValidator(): ValidatorFn {
   return (control: AbstractControl): ValidationErrors | null => {
      const keysArray = control as UntypedFormArray;

      if (!Array.isArray(keysArray.controls) || keysArray.controls.length === 0) {
         return null;
      }

      const keyNumbers = keysArray.controls
         .filter((key) => !key.get('isDeleted')?.value)
         .map((key) => key.get('keyNumber')?.value);

      const duplicateNumbers = keyNumbers.filter((num, index) => keyNumbers.indexOf(num) !== index);

      keysArray.controls.forEach((keyControl) => {
         const keyNumberControl = keyControl.get('keyNumber');
         const keyNumber = keyNumberControl?.value;
         const isDeleted = keyControl.get('isDeleted')?.value;

         if (isDeleted) {
            keyNumberControl?.setErrors(null);
            return;
         }
         if (/^\s*$/.test(keyNumber) && keyControl.touched) {
            keyNumberControl?.setErrors({ required: true });
         } else if (!/^(\*?[0-9]{1}|#|[0-9]{1,2})$/.test(keyNumber) && keyControl.touched) {
            keyNumberControl?.setErrors({ pattern: true });
         } else if (duplicateNumbers.includes(keyNumber)) {
            keyNumberControl?.setErrors({ unique: true });
         } else if (keyNumberControl?.errors) {
            keyNumberControl.setErrors(null);
         }
      });

      return null;
   };
}

export function routeSelectionValidator(): ValidatorFn {
   return (control: AbstractControl): ValidationErrors | null => {
      const routeToExtension = control.get('routeToExtension');
      const routeToNullAction = control.get('routeToNullAction');

      if (
         (routeToExtension?.value == null || routeToExtension?.value === '') &&
         (routeToNullAction?.value == null || routeToNullAction?.value === NullActionTypes.Unset)
      ) {
         routeToExtension?.setValidators(Validators.required);
         return { required: 'Required' };
      }
      return null;
   };
}
